/**
 * Processes and filters corpus data
 */
export class DataProcessor {
  constructor(data) {
    this.corpus1 = data.corpus1;
    this.corpus2 = data.corpus2;
    this.locations = data.locations;

    // Build lookup indexes
    this.buildIndexes();
  }

  buildIndexes() {
    // Validate input data
    if (!Array.isArray(this.corpus1)) {
      console.error('corpus1 is not an array:', this.corpus1);
      this.corpus1 = [];
    }
    if (!Array.isArray(this.corpus2)) {
      console.error('corpus2 is not an array:', this.corpus2);
      this.corpus2 = [];
    }

    // Index items by year
    this.corpus1ByYear = this.groupByYear(this.corpus1);
    this.corpus2ByYear = this.groupByYear(this.corpus2);

    console.log(`Loaded ${this.corpus1.length} items in corpus 1`);
    console.log(`Loaded ${this.corpus2.length} items in corpus 2`);
    console.log(`Loaded ${Object.keys(this.locations).length} locations`);

    // Calculate year range
    const allYears = [
      ...Object.keys(this.corpus1ByYear),
      ...Object.keys(this.corpus2ByYear)
    ].map(Number).filter(y => !isNaN(y));

    if (allYears.length === 0) {
      console.error('No valid years found in corpus data!');
      this.minYear = 2000;
      this.maxYear = 2024;
    } else {
      this.minYear = Math.min(...allYears);
      this.maxYear = Math.max(...allYears);
    }

    console.log(`Year range: ${this.minYear} - ${this.maxYear}`);
    console.log(`Years with data:`, allYears.sort((a, b) => a - b));
  }

  groupByYear(corpus) {
    const grouped = {};
    for (const item of corpus) {
      if (!grouped[item.year]) {
        grouped[item.year] = [];
      }
      grouped[item.year].push(item);
    }
    return grouped;
  }

  getYearRange() {
    return [this.minYear, this.maxYear];
  }

  /**
   * Get data for a specific time window
   */
  getWindowData(startYear, endYear) {
    const metadata1 = this.getLocationCounts(
      this.corpus1ByYear,
      startYear,
      endYear
    );

    const metadata2 = this.getLocationCounts(
      this.corpus2ByYear,
      startYear,
      endYear
    );

    return { metadata1, metadata2 };
  }

  /**
   * Count location occurrences in a time window
   * Returns: { locationId: count, ... }
   */
  getLocationCounts(corpusByYear, startYear, endYear) {
    const locationCounts = {};

    for (let year = startYear; year <= endYear; year++) {
      const items = corpusByYear[year] || [];

      for (const item of items) {
        if (!item.locations || !Array.isArray(item.locations)) {
          console.warn(`Item ${item.id} has invalid locations:`, item.locations);
          continue;
        }

        for (const locationId of item.locations) {
          locationCounts[locationId] = (locationCounts[locationId] || 0) + 1;
        }
      }
    }

    console.log(`Found ${Object.keys(locationCounts).length} unique locations in window ${startYear}-${endYear}`);
    return locationCounts;
  }

  /**
   * Get corpus items for a specific location in a time window
   * @param {string} locationName - Name of the location
   * @param {number} startYear - Start year of window
   * @param {number} endYear - End year of window
   * @returns {Array} Array of items with metadata indicator: {id, year, locations, metadata}
   */
  getItemsForLocation(locationName, startYear, endYear) {
    const items = [];

    // Search in corpus1
    for (let year = startYear; year <= endYear; year++) {
      const yearItems = this.corpus1ByYear[year] || [];
      for (const item of yearItems) {
        if (item.locations && item.locations.includes(locationName)) {
          items.push({
            ...item,
            metadata: 'metadata1'
          });
        }
      }
    }

    // Search in corpus2
    for (let year = startYear; year <= endYear; year++) {
      const yearItems = this.corpus2ByYear[year] || [];
      for (const item of yearItems) {
        if (item.locations && item.locations.includes(locationName)) {
          items.push({
            ...item,
            metadata: 'metadata2'
          });
        }
      }
    }

    // Sort by year (descending), then by id
    items.sort((a, b) => {
      if (a.year !== b.year) {
        return (b.year || 0) - (a.year || 0);
      }
      return a.id.localeCompare(b.id);
    });

    return items;
  }

  /**
   * Get location counts over time (for statistics)
   */
  getLocationCountsOverTime(metadataSet = 'both') {
    const timeline = [];

    for (let year = this.minYear; year <= this.maxYear; year++) {
      const counts = {
        year,
        metadata1: 0,
        metadata2: 0
      };

      if (metadataSet === 'both' || metadataSet === 'metadata1') {
        const items1 = this.corpus1ByYear[year] || [];
        counts.metadata1 = items1.reduce((sum, item) =>
          sum + item.locations.length, 0
        );
      }

      if (metadataSet === 'both' || metadataSet === 'metadata2') {
        const items2 = this.corpus2ByYear[year] || [];
        counts.metadata2 = items2.reduce((sum, item) =>
          sum + item.locations.length, 0
        );
      }

      timeline.push(counts);
    }

    return timeline;
  }

  /**
   * Get location type breakdown for a time window
   * Returns: { metadata1: { type: count }, metadata2: { type: count } }
   */
  getLocationTypeBreakdown(startYear, endYear) {
    const windowData = this.getWindowData(startYear, endYear);

    const breakdown = {
      metadata1: this.countLocationTypes(windowData.metadata1),
      metadata2: this.countLocationTypes(windowData.metadata2)
    };

    return breakdown;
  }

  /**
   * Count locations by type/addresstype
   * @param {Object} locationCounts - { locationId: count }
   * @returns {Object} - { type: count }
   */
  countLocationTypes(locationCounts) {
    const typeCounts = {};

    for (const [locationId, count] of Object.entries(locationCounts)) {
      const location = this.locations[locationId];
      if (!location) continue;

      // Use addresstype if available, fallback to type
      const locationType = location.addresstype || location.type || 'unknown';

      typeCounts[locationType] = (typeCounts[locationType] || 0) + count;
    }

    return typeCounts;
  }

  /**
   * Search locations by name
   */
  searchLocations(query) {
    const lowerQuery = query.toLowerCase();
    const results = [];

    for (const [id, location] of Object.entries(this.locations)) {
      if (location.name.toLowerCase().includes(lowerQuery) ||
          location.display_name.toLowerCase().includes(lowerQuery)) {
        results.push({ id, ...location });
      }
    }

    return results;
  }
}
